import React from 'react';

import { Button, Progress } from 'antd-v5';
import { MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import StatusTag from 'scouting-ui/lib/StatusTag/StatusTag';
import { Text } from 'scouting-ui/lib/Text';
import { cssVars } from 'scouting-ui/lib/variables';

import {
  isStarted as isPastStarted,
  statusColor,
  tagStatus,
  toPercent,
} from '@shared/utils/advancementDisplay';
import advancementStatus from '@shared/utils/advancementStatus';

import AdvancementAuditTooltip, {
  formatAuditDate,
} from '../AdvancementAudit/AdvancementAuditTooltip';
import AdvancementImage from '../AdvancementImage/AdvancementImage';
import styles from './YouthAdvancementsCard.module.scss';
import type { YouthAdvancementItem } from './types';

export interface AdvancementRowProps {
  item: YouthAdvancementItem;
  onViewMore?: (item: YouthAdvancementItem) => void;
}

export default function AdvancementRow(props: AdvancementRowProps) {
  const { item, onViewMore } = props;

  // Derived from the audit fields; the API is inconsistent about `item.status`
  const status = advancementStatus.getStatus(item);
  const tag = tagStatus(status);
  // A `ColorType`, which is what `Text` wants; `Progress` needs the hex
  const color = statusColor(tag);

  const percent = toPercent(item.percentCompleted);
  const completedDate = formatAuditDate(item.dateCompleted ?? item.dateEarned);

  return (
    <div className={styles.row}>
      <AdvancementImage src={item.imageUrl} className={styles.image} />

      <div className={styles.details}>
        <div className={styles.topBar}>
          <Text font="body-body-4" strong className={styles.name}>
            {item.name}
          </Text>

          <div className={styles.meta}>
            {!!completedDate && (
              <Text
                font="body-caption"
                color="color-text-light"
                className={styles.completed}
              >
                <FormattedMessage id="youthAdvancementsCard.completed" />{' '}
                {completedDate}
              </Text>
            )}

            {isPastStarted(status) && (
              <AdvancementAuditTooltip advancement={item}>
                <span className={styles.status}>
                  <StatusTag status={tag} />
                  <MdInfo
                    className={styles.infoIcon}
                    color={cssVars['color-info']}
                  />
                </span>
              </AdvancementAuditTooltip>
            )}

            <Text strong color={color}>
              {`${percent}%`}
            </Text>
          </div>
        </div>

        <div className={styles.progress}>
          <Progress
            percent={percent}
            strokeColor={cssVars[color]}
            strokeLinecap="round"
            showInfo={false}
            size={['100%', 10]}
          />
          {onViewMore && (
            <Button
              size="small"
              type="default"
              className={styles.viewMore}
              onClick={() => onViewMore(item)}
            >
              <FormattedMessage id="youthAdvancementsCard.viewMore" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
